import React, { FunctionComponent } from 'react';

import { range } from 'fp-ts/NonEmptyArray';

import clsx from 'clsx';

import { generatePageUrl, paginationDefaultOptions } from '@utils/route';

import { Link } from '@components/common/Link/Link';
import { ArrowBackIcon, ArrowForwardIcon } from '@components/icons';

import styles from './Pagination.module.scss';

export const calcLastPage = (total: number, pageSize: number): number =>
    Math.ceil(total / pageSize);

type Align = 'center' | 'left';
type Offset = 'top' | 'none';

const alignClassMap: Record<Align, string | undefined> = {
    center: styles.isAlignCenter,
    left: styles.isAlignLeft,
};

const offsetClassMap: Record<Offset, string | undefined> = {
    top: styles.isOffsetTop,
    none: undefined,
};

export type PaginationProps = {
    total: number;
    pageSize: number;
    currentPage?: number;
    options?: {
        currentQuery?: Record<string, string | string[] | undefined>;
        currentPath?: string;
        isFirstAndLastAlwaysVisible?: boolean;
        extremePagesLimit?: number;
        nearbyPagesLimit?: number;
        align?: Align;
        offset?: Offset;
    };
    className?: string;
};

export const Pagination: FunctionComponent<PaginationProps> = (props) => {
    const { total, pageSize, options = {}, currentPage = 1, className } = props;

    const {
        currentQuery,
        currentPath = '',
        isFirstAndLastAlwaysVisible = paginationDefaultOptions.isFirstAndLastAlwaysVisible,
        extremePagesLimit = paginationDefaultOptions.extremePagesLimit,
        nearbyPagesLimit = paginationDefaultOptions.nearbyPagesLimit,
        align = 'center',
        offset = 'top',
    } = options;

    const lastPage = calcLastPage(total, pageSize);

    const makeUrl = generatePageUrl({
        currentQuery,
        currentPath,
    });

    return (
        <ul className={clsx(styles.root, alignClassMap[align], offsetClassMap[offset], className)}>
            {currentPage > 1 ? (
                <>
                    <li className={clsx(styles.item, styles.isPrev)}>
                        <Link
                            route={makeUrl(currentPage - 1)}
                            title="Предыдущая страница"
                            className={styles.inner}
                            rel="prev"
                        >
                            <ArrowBackIcon className={styles.arrow} />
                        </Link>
                    </li>

                    {range(1, extremePagesLimit).map(
                        (pageNumber) =>
                            pageNumber < currentPage - nearbyPagesLimit && (
                                <li
                                    key={`item-${pageNumber}`}
                                    className={clsx(
                                        styles.item,
                                        pageNumber === 1 && styles.isFirstPage,
                                    )}
                                >
                                    <Link
                                        title={`Страница ${pageNumber}`}
                                        route={makeUrl(pageNumber)}
                                        className={styles.inner}
                                    >
                                        {pageNumber}
                                    </Link>
                                </li>
                            ),
                    )}

                    {extremePagesLimit + 1 < currentPage - nearbyPagesLimit && (
                        <li className={clsx(styles.item, styles.isDisabled)}>
                            <span className={styles.inner}>…</span>
                        </li>
                    )}

                    {range(currentPage - nearbyPagesLimit, currentPage - 1).map(
                        (pageNumber) =>
                            pageNumber > 0 && (
                                <li
                                    key={`item-${pageNumber}`}
                                    className={clsx(
                                        styles.item,
                                        pageNumber === 1 && styles.isFirstPage,
                                    )}
                                >
                                    <Link
                                        title={`Страница ${pageNumber}`}
                                        className={styles.inner}
                                        route={makeUrl(pageNumber)}
                                    >
                                        {pageNumber}
                                    </Link>
                                </li>
                            ),
                    )}
                </>
            ) : (
                isFirstAndLastAlwaysVisible && (
                    <li className={clsx(styles.item, styles.isDisabled, styles.isPrev)}>
                        <span className={styles.inner}>
                            <ArrowBackIcon className={styles.arrow} />
                        </span>
                    </li>
                )
            )}

            <li
                className={clsx(
                    styles.item,
                    styles.isActive,
                    currentPage === 1 && styles.isFirstPage,
                )}
            >
                <span className={styles.inner}>{currentPage}</span>
            </li>

            {currentPage < lastPage ? (
                <>
                    {range(currentPage + 1, currentPage + nearbyPagesLimit).map(
                        (pageNumber) =>
                            pageNumber <= lastPage && (
                                <li
                                    key={`item-${pageNumber}`}
                                    className={clsx(
                                        styles.item,
                                        pageNumber === lastPage && styles.isLastPage,
                                    )}
                                >
                                    <Link
                                        route={makeUrl(pageNumber)}
                                        className={styles.inner}
                                        title={`Страница ${pageNumber}`}
                                    >
                                        {pageNumber}
                                    </Link>
                                </li>
                            ),
                    )}

                    {lastPage - extremePagesLimit > currentPage + nearbyPagesLimit && (
                        <li className={clsx(styles.item, styles.isDisabled)}>
                            <span className={styles.inner}>…</span>
                        </li>
                    )}

                    {range(lastPage - extremePagesLimit + 1, lastPage).map(
                        (pageNumber) =>
                            pageNumber > currentPage + nearbyPagesLimit && (
                                <li
                                    className={clsx(
                                        styles.item,
                                        pageNumber === lastPage && styles.isLastPage,
                                    )}
                                    key={`item-${pageNumber}`}
                                >
                                    <Link
                                        className={styles.inner}
                                        route={makeUrl(pageNumber)}
                                        title={`Страница ${pageNumber}`}
                                    >
                                        {pageNumber}
                                    </Link>
                                </li>
                            ),
                    )}

                    <li className={clsx(styles.item, styles.isNext)}>
                        <Link
                            route={makeUrl(currentPage + 1)}
                            title="Следующая страница"
                            className={styles.inner}
                            rel="next"
                        >
                            <ArrowForwardIcon className={styles.arrow} />
                        </Link>
                    </li>
                </>
            ) : (
                isFirstAndLastAlwaysVisible && (
                    <li className={clsx(styles.item, styles.isDisabled, styles.isNext)}>
                        <span className={styles.inner}>
                            <ArrowForwardIcon className={styles.arrow} />
                        </span>
                    </li>
                )
            )}
        </ul>
    );
};
